(function () {
  'use strict';

  class FeaturedCollectionPro {
    constructor(section) {
      this.section = section;
      this.id      = section.dataset.fcpId;
      this.outer   = section.querySelector('[data-fcp-outer]');
      this.track   = section.querySelector('[data-fcp-track]');
      this.nav     = section.querySelector('[data-fcp-nav]');
      if (!this.track || !this.outer) return;

      this.slides  = Array.from(this.track.querySelectorAll('.fcp-slide'));
      if (!this.slides.length) return;

      this.prevBtn = this.nav && this.nav.querySelector('[data-fcp-prev]');
      this.nextBtn = this.nav && this.nav.querySelector('[data-fcp-next]');
      this.curEl   = this.nav && this.nav.querySelector('[data-fcp-cur]');
      this.totEl   = this.nav && this.nav.querySelector('[data-fcp-tot]');

      this.COLS_DSK = parseInt(section.dataset.fcpColsDsk, 10) || 4;
      this.COLS_MOB = parseInt(section.dataset.fcpColsMob, 10) || 2;

      this.page         = 0;
      this.scrolling    = false;
      this.raf          = null;
      this._cleanups    = [];
      this._scrollTimer = null;
      this._resizeTimer = null;
    }

    init() {
      if (!this.track) return;
      this._on(this.prevBtn, 'click', () => this._goTo(this.page - 1));
      this._on(this.nextBtn, 'click', () => this._goTo(this.page + 1));
      this._on(this.track, 'scroll', () => this._onScroll(), { passive: true });
      this._on(window, 'resize', () => this._onResize(), { passive: true });
      this._sync();
    }

    destroy() {
      this._cleanups.forEach(fn => fn());
      this._cleanups = [];
      cancelAnimationFrame(this.raf);
      clearTimeout(this._scrollTimer);
      clearTimeout(this._resizeTimer);
      delete this.section._instance;
    }

    _cols()      { return window.innerWidth >= 750 ? this.COLS_DSK : this.COLS_MOB; }
    _pageCount() { return Math.max(1, Math.ceil(this.slides.length / this._cols())); }

    /**
     * FIX: Return the real scrollLeft target for a page by reading the actual
     * offsetLeft of the first slide on that page.
     *
     * The old approach used `page * outer.clientWidth`, which was wrong because
     * the CSS gap between cards means each slide is narrower than clientWidth/COLS.
     * The gap error accumulated per page, causing the slider to overshoot and show
     * seemingly random numbers of products per page.
     *
     * Reading slide.offsetLeft is gap-proof, padding-proof, and rounding-proof.
     */
    _offsetForPage(p) {
      const index = p * this._cols();
      const slide = this.slides[index];
      if (!slide) return 0;
      // slide.offsetLeft is relative to its offsetParent (the track).
      // Subtract track.offsetLeft in case track itself is offset inside outer.
      return slide.offsetLeft - (this.track.offsetLeft || 0);
    }

    _goTo(p) {
      const total = this._pageCount();
      this.page = Math.max(0, Math.min(p, total - 1));
      this.scrolling = true;
      clearTimeout(this._scrollTimer);
      this._scrollTimer = setTimeout(() => { this.scrolling = false; }, 700);
      this.track.scrollTo({ left: this._offsetForPage(this.page), behavior: 'smooth' });
      this._sync(total);
    }

    _sync(total) {
      total = total != null ? total : this._pageCount();
      if (this.prevBtn) {
        this.prevBtn.disabled = this.page === 0;
        this.prevBtn.setAttribute('aria-disabled', String(this.page === 0));
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = this.page >= total - 1;
        this.nextBtn.setAttribute('aria-disabled', String(this.page >= total - 1));
      }
      if (this.curEl) this.curEl.textContent = this.page + 1;
      if (this.totEl) this.totEl.textContent = total;
    }

    /**
     * FIX: Detect current page by comparing scrollLeft against real slide offsets
     * rather than dividing by clientWidth (which was also gap-broken).
     */
    _onScroll() {
      if (this.scrolling) return;
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        const sl    = this.track.scrollLeft;
        const total = this._pageCount();
        let closest = 0;
        let minDist = Infinity;
        for (let p = 0; p < total; p++) {
          const dist = Math.abs(sl - this._offsetForPage(p));
          if (dist < minDist) { minDist = dist; closest = p; }
        }
        if (closest !== this.page) { this.page = closest; this._sync(); }
      });
    }

    /**
     * FIX: On resize, re-scroll to the correct offset for the current page
     * using the new (recalculated) slide positions after layout reflow.
     */
    _onResize() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        this.track.scrollTo({ left: this._offsetForPage(this.page), behavior: 'auto' });
        this._sync();
      }, 200);
    }

    _on(el, ev, fn, opts) {
      if (!el) return;
      el.addEventListener(ev, fn, opts);
      this._cleanups.push(() => el.removeEventListener(ev, fn, opts));
    }
  }

  function mount(section) {
    if (section._instance) return;
    const c = new FeaturedCollectionPro(section);
    c.init();
    section._instance = c;
  }

  function unmount(section) {
    section._instance?.destroy();
  }

  function observeAll() {
    document.querySelectorAll('[data-fcp-section]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeAll, { once: true });
  } else {
    observeAll();
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', ({ detail }) => {
      const s = document.querySelector(`[data-fcp-section][data-fcp-id="${detail.sectionId}"]`);
      if (!s) return;
      unmount(s);
      setTimeout(() => mount(s), 80);
    });
    document.addEventListener('shopify:section:unload', ({ detail }) => {
      const s = document.querySelector(`[data-fcp-section][data-fcp-id="${detail.sectionId}"]`);
      if (s) unmount(s);
    });
  }

}());